import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useParams, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { Layout } from '@/client/layouts/layout';
import {
  DetailHeader,
  DetailCard,
  DetailGrid,
  DetailList,
  DetailRow,
  CopyableText,
  BooleanBadge,
} from '@/client/components/ui/detail-view';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { Progress } from '@/client/components/ui/progress';
import { RefreshCw, Loader2, ExternalLink } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';

interface RDSInstance {
  identifier: string;
  status: string;
  engine: string;
  engineVersion: string;
  instanceClass: string;
  allocatedStorage: number;
  endpoint: {
    address: string;
    port: number;
  } | null;
  availabilityZone: string;
  multiAZ: boolean;
  storageType: string;
  iops?: number;
}

interface RDSMetrics {
  identifier: string;
  cpu?: number;
  connections?: number;
  freeableMemory?: number;
  freeStorageSpace?: number;
  readIOPS?: number;
  writeIOPS?: number;
}

interface RDSInstanceWithMetrics extends RDSInstance {
  metrics?: RDSMetrics;
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'aws/rds/$instanceId',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: RDSDetailComponent,
});

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'available' ? 'default' :
                  status === 'stopped' ? 'secondary' :
                  ['failed', 'storage-full'].includes(status) ? 'destructive' :
                  'outline';

  return (
    <Badge variant={variant} className={status === 'available' ? 'bg-green-600' : ''}>
      {status}
    </Badge>
  );
}

function CPUCell({ cpu }: { cpu?: number }) {
  if (cpu === undefined) return <span className="text-muted-foreground">-</span>;

  const color = cpu > 80 ? 'bg-red-500' : cpu > 60 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="flex items-center gap-2">
      <Progress value={cpu} className="h-2 w-16" indicatorClassName={color} />
      <span className="text-sm">{cpu}%</span>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function RDSDetailComponent() {
  const { instanceId } = useParams({ from: '/aws/rds/$instanceId' });
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const [instance, setInstance] = useState<RDSInstanceWithMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      if (forceRefresh) {
        await Promise.all([
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:rds:instances' }),
          }),
          fetch(`${API_BASE_URL}/api/cache/invalidate`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: 'aws:rds:metrics' }),
          }),
        ]);
      }

      const [instancesRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/aws/rds/instances`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/aws/rds/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!instancesRes.ok) {
        const err = await instancesRes.json();
        throw new Error(err.message || 'Failed to fetch RDS instances');
      }

      const instancesData = await instancesRes.json();
      const metricsData = metricsRes.ok ? await metricsRes.json() : { metrics: [] };

      // Find the matching instance
      const foundInstance = instancesData.instances.find(
        (i: RDSInstance) => i.identifier === instanceId
      );

      if (foundInstance) {
        const metricsForInstance = metricsData.metrics.find(
          (m: RDSMetrics) => m.identifier === instanceId
        );
        setInstance({
          ...foundInstance,
          metrics: metricsForInstance,
        });
      } else {
        setError('Instance not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [instanceId, getAccessTokenSilently]);

  // Loading state
  if (loading && !instance) {
    return (
      <Layout>
        <DetailHeader
          title="Loading..."
          backTo="/aws/rds-health"
          backLabel="Back to RDS Health"
        />
        <div className="rounded-lg border p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-4">Loading instance data...</p>
        </div>
      </Layout>
    );
  }

  // Not found state
  if (!instance || error) {
    return (
      <Layout>
        <DetailHeader
          title="Instance Not Found"
          backTo="/aws/rds-health"
          backLabel="Back to RDS Health"
        />
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground mb-4">
            {error || 'This RDS instance could not be found.'}
          </p>
          <Button variant="outline" onClick={() => navigate({ to: '/aws/rds-health' })}>
            Return to RDS Health
          </Button>
        </div>
      </Layout>
    );
  }

  const m = instance.metrics;

  return (
    <Layout>
      <DetailHeader
        title={instance.identifier}
        backTo="/aws/rds-health"
        backLabel="Back to RDS Health"
        badge={<StatusBadge status={instance.status} />}
        actions={
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-black hover:text-white"
              title="View in AWS Console"
              asChild
            >
              <a
                href={`https://console.aws.amazon.com/rds/home?region=${instance.availabilityZone.slice(0, -1)}#database:id=${instance.identifier};is-cluster=false`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in AWS
                <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </a>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Refresh"
              onClick={() => fetchData(true)}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        }
      />

      <DetailGrid columns={2}>
        {/* Instance Info */}
        <DetailCard title="Instance Info">
          <DetailList>
            <DetailRow
              label="Identifier"
              value={<CopyableText text={instance.identifier} />}
            />
            <DetailRow label="Status" value={<StatusBadge status={instance.status} />} />
            <DetailRow label="Engine" value={`${instance.engine} ${instance.engineVersion}`} />
            <DetailRow label="Instance Class" value={instance.instanceClass} mono />
            <DetailRow label="Availability Zone" value={instance.availabilityZone} />
            <DetailRow label="Multi-AZ" value={<BooleanBadge value={instance.multiAZ} />} />
          </DetailList>
        </DetailCard>

        {/* Endpoint */}
        <DetailCard title="Endpoint">
          <DetailList>
            {instance.endpoint ? (
              <>
                <DetailRow
                  label="Address"
                  value={<CopyableText text={instance.endpoint.address} />}
                />
                <DetailRow label="Port" value={instance.endpoint.port} mono />
              </>
            ) : (
              <DetailRow label="Status" value="No endpoint available" />
            )}
          </DetailList>
        </DetailCard>

        {/* Storage */}
        <DetailCard title="Storage">
          <DetailList>
            <DetailRow label="Allocated" value={`${instance.allocatedStorage} GB`} />
            <DetailRow label="Type" value={instance.storageType} />
            {instance.iops && <DetailRow label="Provisioned IOPS" value={instance.iops} />}
            {m?.freeStorageSpace !== undefined && (
              <DetailRow label="Free Space" value={formatBytes(m.freeStorageSpace)} />
            )}
          </DetailList>
        </DetailCard>

        {/* Current Metrics */}
        <DetailCard title="Current Metrics">
          <DetailList>
            <DetailRow
              label="CPU"
              value={m?.cpu !== undefined ? <CPUCell cpu={m.cpu} /> : '-'}
            />
            <DetailRow
              label="Connections"
              value={m?.connections !== undefined ? m.connections : '-'}
            />
            <DetailRow
              label="Free Memory"
              value={m?.freeableMemory !== undefined ? formatBytes(m.freeableMemory) : '-'}
            />
            <DetailRow
              label="Read IOPS"
              value={m?.readIOPS !== undefined ? m.readIOPS : '-'}
            />
            <DetailRow
              label="Write IOPS"
              value={m?.writeIOPS !== undefined ? m.writeIOPS : '-'}
            />
          </DetailList>
        </DetailCard>
      </DetailGrid>
    </Layout>
  );
}
